import React, { useState, useEffect } from 'react';
import { Megaphone, Mail, Gift, ShoppingCart, Calendar, Settings, Play, Pause, ChevronRight, Send, Package, Clock, Users, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import AdminPageHeader from '../../components/AdminPageHeader';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';
import { formatEUR } from '../../utils/format';

const CampaignManager = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [highlightsDialogOpen, setHighlightsDialogOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchCampaigns();
    fetchProducts();
  }, []);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/campaigns/admin/all');
      setCampaigns(response.data || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Kampagnen');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axiosInstance.get('/products?active_only=true');
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const toggleCampaign = async (campaignId, enabled) => {
    try {
      await axiosInstance.put(`/campaigns/admin/${campaignId}`, { enabled });
      toast.success(enabled ? 'Kampagne aktiviert' : 'Kampagne deaktiviert');
      fetchCampaigns();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const updateCampaignSettings = async (campaignId, settings) => {
    try {
      await axiosInstance.put(`/campaigns/admin/${campaignId}`, { settings });
      toast.success('Einstellungen gespeichert');
      setEditDialogOpen(false);
      fetchCampaigns();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const sendBirthdayCoupons = async () => {
    setSending(true);
    try {
      const response = await axiosInstance.post('/campaigns/admin/birthday/check-and-send');
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };

  const sendCartReminders = async () => {
    setSending(true);
    try {
      const response = await axiosInstance.post('/campaigns/admin/cart-reminder/check-and-send');
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };

  const autoSelectHighlights = async () => {
    try {
      const response = await axiosInstance.post('/campaigns/admin/monthly-highlights/auto-select');
      const productIds = response.data.products.map(p => p.id);
      setSelectedProducts(productIds);
      toast.success(`${response.data.count} Produkte ausgewählt (${response.data.criteria})`);
    } catch (error) {
      toast.error('Fehler bei der automatischen Auswahl');
    }
  };

  const sendHighlights = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Bitte wählen Sie mindestens ein Produkt aus');
      return;
    }
    
    setSending(true);
    try {
      const response = await axiosInstance.post('/campaigns/admin/monthly-highlights/send', {
        product_ids: selectedProducts
      });
      toast.success(response.data.message);
      setHighlightsDialogOpen(false);
      setSelectedProducts([]);
    } catch (error) {
      toast.error('Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };

  const getCampaignIcon = (id) => {
    switch (id) {
      case 'welcome_email': 
        return (
          <div className="p-2 bg-emerald-50/70 rounded-none">
            <Mail className="h-5 w-5 text-emerald-500" />
          </div>
        );
      case 'birthday_coupon': 
        return (
          <div className="p-2 bg-rose-50/70 rounded-none">
            <Gift className="h-5 w-5 text-rose-500" />
          </div>
        );
      case 'cart_reminder': 
        return (
          <div className="p-2 bg-amber-50/70 rounded-none">
            <ShoppingCart className="h-5 w-5 text-amber-500" />
          </div>
        );
      case 'monthly_highlights': 
        return (
          <div className="p-2 bg-violet-50/70 rounded-none">
            <Calendar className="h-5 w-5 text-violet-500" />
          </div>
        );
      default: 
        return (
          <div className="p-2 bg-sky-50/70 rounded-none">
            <Megaphone className="h-5 w-5 text-sky-500" />
          </div>
        );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nie';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen py-4 px-3 sm:px-4" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-6xl mx-auto">
        <AdminPageHeader 
          title="Kampagnen-Manager" 
          subtitle="Automatische Marketing-Aktionen verwalten"
          icon={Megaphone}
        />

        {/* Section: Info - Goldbraune Überschrift */}
        <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
          <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0F8FF]">
            <Settings className="h-4 w-4 text-[#60A0D0]" />
          </div>
          <h2 className="text-base font-semibold text-white">Hinweise</h2>
        </div>

        <div className="bg-amber-50/70 border border-amber-100 p-3 rounded-none mb-4 text-sm">
          <p className="font-medium text-amber-800">Was ist der Kampagnen-Manager?</p>
          <p className="text-amber-700 mt-1">
            Hier können Sie automatische Marketing-Aktionen ein- und ausschalten. 
            Willkommens-E-Mails, Geburtstags-Gutscheine und Warenkorb-Erinnerungen 
            werden automatisch versendet, wenn sie aktiviert sind.
          </p>
        </div>

        {/* Section: Campaigns - Goldbraune Überschrift */}
        <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
          <div className="w-6 h-6 rounded flex items-center justify-center bg-[#FFF0F5]">
            <Megaphone className="h-4 w-4 text-[#D070A0]" />
          </div>
          <h2 className="text-base font-semibold text-white">Kampagnen-Liste</h2>
        </div>

        {loading ? (
          <p className="text-center py-8 text-gray-500">Lädt...</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {campaigns.map((campaign) => (
              <Card key={campaign.id} className="rounded-none">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getCampaignIcon(campaign.id)}
                      <div>
                        <CardTitle className="text-base">{campaign.name}</CardTitle>
                        <p className="text-xs text-gray-500">{campaign.type === 'automatic' ? 'Automatisch' : 'Geplant'}</p>
                      </div>
                    </div>
                    <Switch
                      checked={campaign.enabled}
                      onCheckedChange={(checked) => toggleCampaign(campaign.id, checked)}
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-3">{campaign.description}</p>
                  
                  {/* Stats */}
                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                    <div className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      <span>{campaign.stats?.sent_count || 0} gesendet</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>Zuletzt: {formatDate(campaign.stats?.last_sent)}</span>
                    </div>
                  </div>

                  {/* Settings Display */}
                  {campaign.settings && (
                    <div className="bg-gray-50 p-2 rounded-none text-xs mb-3">
                      {campaign.id === 'welcome_email' && (
                        <p>Willkommens-Gutschein: {campaign.settings.coupon_percent}%</p>
                      )}
                      {campaign.id === 'birthday_coupon' && (
                        <>
                          <p>Rabatt: {campaign.settings.coupon_percent}%</p>
                          <p>Gültig: {campaign.settings.valid_days} Tage</p>
                        </>
                      )}
                      {campaign.id === 'cart_reminder' && (
                        <>
                          <p>Verzögerung: {campaign.settings.delay_hours} Stunden</p>
                          <p>Min. Warenkorbwert: {formatEUR(campaign.settings.min_cart_value)}</p>
                        </>
                      )}
                      {campaign.id === 'monthly_highlights' && (
                        <>
                          <p>Sendetag: {campaign.settings.send_day}. des Monats</p>
                          <p>Max. Produkte: {campaign.settings.max_products}</p>
                          <p>Auto-Auswahl: {campaign.settings.auto_select ? 'Ja' : 'Nein'}</p>
                        </>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-none flex-1"
                      onClick={() => {
                        setSelectedCampaign(campaign);
                        setEditDialogOpen(true);
                      }}
                    >
                      <Settings className="h-3 w-3 mr-1" />
                      Einstellungen
                    </Button>
                    
                    {campaign.id === 'birthday_coupon' && (
                      <Button
                        size="sm"
                        className="rounded-none"
                        className="rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0"
                        onClick={sendBirthdayCoupons}
                        disabled={!campaign.enabled || sending}
                      >
                        <Send className="h-3 w-3 mr-1" />
                        Jetzt prüfen
                      </Button>
                    )}
                    
                    {campaign.id === 'cart_reminder' && (
                      <Button
                        size="sm"
                        className="rounded-none"
                        className="rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0"
                        onClick={sendCartReminders}
                        disabled={!campaign.enabled || sending}
                      >
                        <Send className="h-3 w-3 mr-1" />
                        Jetzt prüfen
                      </Button>
                    )}
                    
                    {campaign.id === 'monthly_highlights' && (
                      <Button
                        size="sm"
                        className="rounded-none"
                        className="rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0"
                        onClick={() => setHighlightsDialogOpen(true)}
                        disabled={!campaign.enabled}
                      >
                        <Send className="h-3 w-3 mr-1" />
                        Manuell senden
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Settings Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="rounded-none max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                {selectedCampaign?.name} - Einstellungen
              </DialogTitle>
            </DialogHeader>
            
            {selectedCampaign && (
              <div className="space-y-4">
                {selectedCampaign.id === 'welcome_email' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Willkommens-Gutschein (%)</label>
                    <Input
                      type="number"
                      defaultValue={selectedCampaign.settings?.coupon_percent || 10}
                      onChange={(e) => setSelectedCampaign({
                        ...selectedCampaign,
                        settings: { ...selectedCampaign.settings, coupon_percent: parseInt(e.target.value) }
                      })}
                      className="rounded-none"
                    />
                  </div>
                )}

                {selectedCampaign.id === 'birthday_coupon' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1">Rabatt (%)</label>
                      <Input
                        type="number"
                        defaultValue={selectedCampaign.settings?.coupon_percent || 15}
                        onChange={(e) => setSelectedCampaign({
                          ...selectedCampaign,
                          settings: { ...selectedCampaign.settings, coupon_percent: parseInt(e.target.value) }
                        })}
                        className="rounded-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Gültigkeit (Tage)</label>
                      <Input
                        type="number"
                        defaultValue={selectedCampaign.settings?.valid_days || 14}
                        onChange={(e) => setSelectedCampaign({
                          ...selectedCampaign,
                          settings: { ...selectedCampaign.settings, valid_days: parseInt(e.target.value) }
                        })}
                        className="rounded-none"
                      />
                    </div>
                  </>
                )}

                {selectedCampaign.id === 'cart_reminder' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1">Verzögerung (Stunden)</label>
                      <Input
                        type="number"
                        defaultValue={selectedCampaign.settings?.delay_hours || 24}
                        onChange={(e) => setSelectedCampaign({
                          ...selectedCampaign,
                          settings: { ...selectedCampaign.settings, delay_hours: parseInt(e.target.value) }
                        })}
                        className="rounded-none"
                      />
                      <p className="text-xs text-gray-500 mt-1">Nach wie vielen Stunden soll die Erinnerung gesendet werden?</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Min. Warenkorbwert (€)</label>
                      <Input
                        type="number"
                        defaultValue={selectedCampaign.settings?.min_cart_value || 10}
                        onChange={(e) => setSelectedCampaign({
                          ...selectedCampaign,
                          settings: { ...selectedCampaign.settings, min_cart_value: parseInt(e.target.value) }
                        })}
                        className="rounded-none"
                      />
                    </div>
                  </>
                )}

                {selectedCampaign.id === 'monthly_highlights' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1">Sendetag des Monats</label>
                      <Input
                        type="number"
                        min="1"
                        max="28"
                        defaultValue={selectedCampaign.settings?.send_day || 28}
                        onChange={(e) => setSelectedCampaign({
                          ...selectedCampaign,
                          settings: { ...selectedCampaign.settings, send_day: parseInt(e.target.value) }
                        })}
                        className="rounded-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Max. Produkte</label>
                      <Input
                        type="number"
                        min="1"
                        max="12"
                        defaultValue={selectedCampaign.settings?.max_products || 6}
                        onChange={(e) => setSelectedCampaign({
                          ...selectedCampaign,
                          settings: { ...selectedCampaign.settings, max_products: parseInt(e.target.value) }
                        })}
                        className="rounded-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Auswahlkriterium</label>
                      <select
                        defaultValue={selectedCampaign.settings?.selection_criteria || 'bestsellers'}
                        onChange={(e) => setSelectedCampaign({
                          ...selectedCampaign,
                          settings: { ...selectedCampaign.settings, selection_criteria: e.target.value }
                        })}
                        className="w-full border rounded-none p-2"
                      >
                        <option value="bestsellers">Bestseller</option>
                        <option value="newest">Neueste</option>
                        <option value="random">Zufällig</option>
                      </select>
                    </div>
                  </>
                )}

                <Button
                  onClick={() => updateCampaignSettings(selectedCampaign.id, selectedCampaign.settings)}
                  className="w-full rounded-none"
                  className="rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Speichern
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Monthly Highlights Dialog */}
        <Dialog open={highlightsDialogOpen} onOpenChange={setHighlightsDialogOpen}>
          <DialogContent className="rounded-none max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Monatliche Highlights versenden
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-none"
                  onClick={autoSelectHighlights}
                >
                  Automatisch auswählen
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-none"
                  onClick={() => setSelectedProducts([])}
                >
                  Auswahl leeren
                </Button>
                <span className="text-sm text-gray-500 ml-auto self-center">
                  {selectedProducts.length} Produkte ausgewählt
                </span>
              </div>

              <div className="flex-1 overflow-y-auto border rounded-none">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-2">
                  {products.map((product) => (
                    <div
                      key={product.id}
                      className={`border p-2 cursor-pointer transition-colors ${
                        selectedProducts.includes(product.id) 
                          ? 'border-amber-500 bg-amber-50' 
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        setSelectedProducts(prev => 
                          prev.includes(product.id)
                            ? prev.filter(id => id !== product.id)
                            : [...prev, product.id]
                        );
                      }}
                    >
                      {product.images?.[0] && (
                        <img 
                          src={product.images[0]} 
                          alt={product.name}
                          className="w-full h-20 object-cover mb-1"
                        />
                      )}
                      <p className="text-xs font-medium line-clamp-2">{product.name}</p>
                      <p className="text-xs text-gray-500">{formatEUR(product.price)}</p>
                    </div>
                  ))}
                </div>
              </div>

              <Button
                onClick={sendHighlights}
                disabled={selectedProducts.length === 0 || sending}
                className="w-full rounded-none"
                className="rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0"
              >
                <Send className="h-4 w-4 mr-2" />
                {sending ? 'Wird gesendet...' : `An alle Abonnenten senden (${selectedProducts.length} Produkte)`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CampaignManager;
